/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SentryStateMap } from './types';
import {
  getUrlHost,
  pickDimension,
  pickEntities,
  pickList,
  pickRecord,
} from './utils';
import type { CommonModuleState } from '../common/types';
import type { SynchronizationGroupModuleState } from '../generic/syncGroups/types';
import type { WebMapStateModuleState } from '../map/types';
import type { UIModuleState } from '../ui/types';

type StoreSentryModuleState = CommonModuleState &
  WebMapStateModuleState &
  SynchronizationGroupModuleState &
  UIModuleState;

export const storeSentryStateMap: SentryStateMap<StoreSentryModuleState> = {
  common: (common) => ({
    auth: { isLoggedIn: common.auth?.isLoggedIn },
  }),
  webmap: (webmap) =>
    pickEntities(webmap, (map) => ({
      id: map.id,
      srs: map.srs,
      isAnimating: map.isAnimating,
      isAutoUpdating: map.isAutoUpdating,
      animationStartTime: map.animationStartTime,
      animationEndTime: map.animationEndTime,
      animationDelay: map.animationDelay,
      timeStep: map.timeStep,
      isTimestepAuto: map.isTimestepAuto,
      mapLayers: map.mapLayers,
      baseLayers: map.baseLayers,
      overLayers: map.overLayers,
      featureLayers: map.featureLayers,
      autoUpdateLayerId: map.autoUpdateLayerId,
      autoTimeStepLayerId: map.autoTimeStepLayerId,
      dimensions: pickList(map.dimensions, pickDimension),
    })),
  layers: (layers) =>
    pickEntities(layers, (layer) => ({
      id: layer.id,
      mapId: layer.mapId,
      name: layer.name,
      layerType: layer.layerType,
      service: getUrlHost(layer.service),
      enabled: layer.enabled,
      opacity: layer.opacity,
      style: layer.style,
      format: layer.format,
      status: layer.status,
      useLatestReferenceTime: layer.useLatestReferenceTime,
      dimensions: pickList(layer.dimensions, pickDimension),
    })),
  services: (services) =>
    pickEntities(services, (service) => ({
      id: service.id,
      serviceUrl: getUrlHost(service.serviceUrl),
      active: service.active,
      scope: service.scope,
      type: service.type,
    })),
  syncGroups: (syncGroups) => ({
    isTimeScrollingEnabled: syncGroups.isTimeScrollingEnabled,
    groups: pickEntities(syncGroups.groups, (group) => ({
      type: group.type,
      sourcePanelId: group.sourcePanelId,
      targets: pickEntities(group.targets, (target) => target),
    })),
    sources: pickEntities(syncGroups.sources, (source) => ({
      types: source.types,
      targets: pickEntities(source.targets, (target) => target),
    })),
  }),
  ui: (ui) => ({
    order: ui.order,
    activeWindowId: ui.activeWindowId,
    dialogs: pickRecord(ui.dialogs, (dialog) => ({
      type: dialog.type,
      activeMapId: dialog.activeMapId,
      isOpen: dialog.isOpen,
      isLoading: dialog.isLoading,
      error: dialog.error,
      source: dialog.source,
      sourcePanelId: dialog.sourcePanelId,
    })),
  }),
};
